/* Copyright (c) 2020-present CloudIO Inc */

import { ExpressionOnlyValue } from './Component.type';

export const expressionTypes = {
  text: 'Text',
  expression: 'Expression',
} as const;

export const propertyValueTypes = {
  static: 'Static',
  expression: 'Expression',
} as const;

export const buttonVariant = {
  contained: 'Contained',
  outlined: 'Outlined',
  text: 'Text',
} as const;

export const gridColumnAlignment = {
  start: 'Start',
  center: 'Center',
  end: 'End',
} as const;

export const avatarVariant = {
  circular: 'Circular',
  rounded: 'Rounded',
  square: 'Square',
} as const;

export const messageType = {
  error: 'Error',
  info: 'Info',
  success: 'Success',
  warning: 'Warning',
} as const;

export const dataLevelType = {
  page: 'Page',
  global: 'Global',
  localStorage: 'Local Storage',
} as const;

export const OutAttrLevelType = {
  dataSource: 'Data Source',
  page: 'Page',
  global: 'Global',
} as const;

export const buttonSize = {
  large: 'Large',
  medium: 'Medium',
  small: 'Small',
} as const;

export const importFileType = {
  csv: 'CSV',
  xlsx: 'Excel',
} as const;

export const iconButtonSize = {
  medium: 'Medium',
  small: 'Small',
} as const;

export const chipVariant = {
  filled: 'Filled',
  outlined: 'Outlined',
} as const;

export const iconButtonColor = {
  default: 'Default',
  primary: 'Primary',
  secondary: 'Secondary',
  info: 'Info',
  success: 'Success',
  warning: 'Warning',
  error: 'Error',
  inherit: 'Inherit',
} as const;

export const gridSize = {
  1: 1,
  2: 2,
  3: 3,
  4: 4,
  5: 5,
  6: 6,
  7: 7,
  8: 8,
  9: 9,
  10: 10,
  11: 11,
  12: 12,
  auto: 'auto',
  false: false,
  true: true,
} as const;

export const aggregateFns = {
  sum: 'sum',
  avg: 'avg',
  count: 'count',
  max: 'max',
  min: 'min',
} as const;

export const chartTypes = {
  bar: 'Bar',
  // bubble: 'Bubble',
  doughnut: 'Doughnut',
  line: 'Line',
  pie: 'Pie',
  polarArea: 'Polar Area',
  radar: 'Radar',
  // scatter: 'Scatter',
} as const;

export const chartDatalabelsAlign = {
  top: 'Top',
  right: 'Right',
  bottom: 'Bottom',
  left: 'Left',
  start: 'Start',
  center: 'Center',
  end: 'End',
} as const;

export const chartDatalabelsAnchor = {
  start: 'Start',
  center: 'Center',
  end: 'End',
} as const;

export const gridWrap = {
  nowrap: 'No Wrap',
  wrap: 'Wrap',
  'wrap-reverse': 'Wrap Reverse',
} as const;

export const gridDirection = {
  column: 'Column',
  'column-reverse': 'Column Reverse',
  row: 'Row',
  'row-reverse': 'Row Reverse',
} as const;

export const cssGridRenderAs = {
  columns: 'Columns',
  rows: 'Rows',
} as const;

export const cssGridSizeType = {
  auto: 'Auto',
  flex: 'Flex',
  minmax: 'Min Max',
  percentage: 'Percentage',
} as const;

export const heightType = {
  auto: 'Auto',
  px: 'px',
  '%': '%',
} as const;

export type HeightType = keyof typeof heightType;

export const cssGridOverflow = {
  scroll: 'Scroll',
  hidden: 'Hidden',
} as const;

export const gridAlignContent = {
  center: 'Center',
  'flex-end': 'Flex End',
  'flex-start': 'Flex Start',
  'space-around': 'Space Around',
  'space-between': 'Space Between',
  stretch: 'Stretch',
} as const;

export const gridJustify = {
  center: 'Center',
  'flex-end': 'Flex End',
  'flex-start': 'Flex Start',
  'space-around': 'Space Around',
  'space-between': 'Space Between',
  'space-evenly': 'Space Evenly',
  stretch: 'Stretch',
} as const;

export const gridAlignItems = {
  baseline: 'Baseline',
  center: 'Center',
  'flex-end': 'Flex End',
  'flex-start': 'Flex Start',
  stretch: 'Stretch',
} as const;

export const themeColor = {
  primary: 'Primary',
  secondary: 'Secondary',
  info: 'Info',
  success: 'Success',
  warning: 'Warning',
  error: 'Error',
  default: 'Default',
  inherit: 'Inherit',
  paper: 'Paper',
  ioGrey: 'Grey',
} as const;

export const typographyVariant = {
  body1: 'Body 1',
  body2: 'Body 2',
  button: 'Button',
  caption: 'Caption',
  h1: 'H1',
  h2: 'H2',
  h3: 'H3',
  h4: 'H4',
  h5: 'H5',
  h6: 'H6',
  overline: 'Overline',
  subtitle1: 'Subtitle 1',
  subtitle2: 'Subtitle 2',
} as const;

export const typographyDisplay = {
  block: 'Block',
  initial: 'Initial',
  inline: 'Inline',
} as const;

export const typographyAlign = {
  center: 'Center',
  inherit: 'Inherit',
  justify: 'Justify',
  left: 'Left',
  right: 'Right',
} as const;

export const fieldVariant = {
  filled: 'Filled',
  outlined: 'Outlined',
  standard: 'Standard',
} as const;

export const notificationLocation = {
  'bottom-right': 'Bottom Right',
  bottom: 'Bottom',
  'top-right': 'Top Right',
  top: 'Top',
} as const;

export const paperVariant = {
  elevation: 'Elevation',
  outlined: 'Outlined',
} as const;

export const textInputOptions = {
  text: 'Text',
  password: 'Password',
};

export const tabPanelIndicatorColor = {
  primary: 'Primary',
  secondary: 'Secondary',
};

export const tabPanelTextColor = { primary: 'Primary', secondary: 'Secondary' };

export const tabPanelVariant = {
  standard: 'Standard',
  fullWidth: 'Full Width',
  scrollable: 'Scrollable',
};

export const tabPanelOrientation = {
  horizontal: 'Horizontal',
  vertical: 'Vertical',
};

export const checkboxSize = {
  medium: 'Medium',
  small: 'Small',
} as const;

export const badgeVariant = {
  dot: 'dot',
  standard: 'standard',
} as const;

export const verticalSplitFixedSide = {
  start: 'Start',
  end: 'End',
} as const;

export const mediaTypes = {
  img: 'Image',
  iframe: 'IFrame',
} as const;

export const labelAlignment = {
  left: 'Left',
  center: 'Center',
  right: 'Right',
} as const;

export const labelPlacement = {
  start: 'Start',
  end: 'End',
};

export const backgroundColor = {
  default: 'Default',
  paper: 'Paper',
  transparent: 'Transparent',
};

export const toolTipPosition = {
  top: 'Top',
  bottom: 'Bottom',
  left: 'Left',
  right: 'Right',
};

export type ToolTipPosition = keyof typeof toolTipPosition;

export const YNValues = {
  checkedValue: 'Y',
  unCheckedValue: 'N',
};

export const TFValues = {
  checkedValue: 'T',
  unCheckedValue: 'F',
};

export const bingMapType = {
  aerial: 'Aerial',
  canvasDark: 'Canvas Dark',
  canvasLight: 'Canvas Light',
  birdseye: 'Birds Eye',
  grayscale: 'Gray Scale',
  mercator: 'Mercator',
  ordnanceSurvay: 'Ordnance Survay',
  road: 'Road',
  streetside: 'Street Side',
};

export const bingMapNavigatorTravelMode = {
  driving: 'Driving',
  walking: 'Walking',
  transit: 'Transit',
};

export const infoBoxShowOn = {
  hover: 'Hover',
  click: 'Click',
};

export const googleMapType = {
  roadmap: 'Road Map',
  satellite: 'Satellite',
  hybrid: 'Hybrid',
  terrian: 'Terrian',
};

export const SINGLETON = '$singleton$';

export interface BaseUserPic {
  userName?: ExpressionOnlyValue;
  size?: number;
  showUserName?: boolean;
  showEmailAddress?: boolean;
  hideOnlineStatus?: boolean;
  hideImage?: boolean;
}

export const BaseUserPicDefaultValue: Required<BaseUserPic> = {
  userName: '',
  size: 40,
  showUserName: false,
  showEmailAddress: false,
  hideOnlineStatus: false,
  hideImage: false,
};

export const toolbarLayoutRTE = {
  hovered: 'Hovered',
  static: 'Static',
} as const;
